import * as Yup from "yup";
import React, { useEffect, useMemo, useState } from "react";
import formatCnpj from "@brazilian-utils/format-cnpj";
import formatCpf from "@brazilian-utils/format-cpf";
import { AiOutlineArrowRight } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";

import ContainedButton from "components/layout/form/containedButton";
import DebitAlertModal from "../DebitAlertModal";
import DebitDeleteModal from "../DebitDeleteModal";
import DebitFailModal from "../DebitFailModal";
import DebitSuccessModal from "../DebitSuccessModal";
import Loading from "components/layout/loading";
import TextField from "components/layout/form/textField";
import TextSelect from "components/layout/form/TextSelect";
import api from "services/api";
import { bancos } from "../bancos";
import { cpfCnpjCursorAlign } from "utils/functions";
import {
  setUnitsList,
  toggleSelectedUnit,
} from "store/actions/select-unit-actions";

import {
  Body,
  ContainedButtonWarn,
  Container,
  Form,
  FormItem,
  Header,
  Subtitle,
  Title,
} from "./styles";

const DebitoAutomaticoForm = (): JSX.Element => {
  const [listBanks, setListBanks] = useState([]);
  const [status, setStatus] = useState("new");
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModalAlert, setIsOpenModalAlert] = useState(false);
  const [isOpenModalSuccess, setIsOpenModalSuccess] = useState(false);
  const [isOpenModalFail, setIsOpenModalFail] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalDebit, setOpenModalDebit] = useState(false);

  const dispatch = useDispatch();
  const { selectedUnit, unitsList } = useSelector((state) => ({
    // @ts-ignore
    selectedUnit: state.selectUnitReducer.selectedUnit,
    // @ts-ignore
    unitsList: state.selectUnitReducer.unitsList,
  }));

  const validationSchema = (): any =>
    Yup.object().shape({
      bankId: Yup.string(),
      accountOwnerName: Yup.string()
        .trim()
        .required("É necessário digitar o nome do titular da conta."),
      accountDocument: Yup.string()
        .trim()
        .required("É necessário digitar o CPF do titular da conta."),
      branch: Yup.string()
        .trim()
        .max(4, "Digite no máximo 4 dígitos.")
        .required("É necessário digitar o número da agência."),
      account: Yup.string()
        .trim()
        .when("bankId", {
          is: (bankId) => bankId === "748",
          then: Yup.string()
            .matches(
              /^\d{5}$/,
              "A conta corrente do Sicredi deve ter 5 números (ex: 12345)"
            )
            .required("É necessário digitar o número da conta."),
          otherwise: Yup.string()
            .max(20, "Digite no máximo 20 dígitos.")
            .required("É necessário digitar o número da conta."),
        }),
      accountCheckDigit: Yup.string().when([], {
        is: () => formik.values.bankId !== "001",
        then: Yup.string()
          .trim()
          .max(1, "Digite no máximo 1 dígito.")
          .required("É necessário inserir o número do dígito da conta."),
      }),
    });

  const formik = useFormik({
    initialValues: {
      bankId: "341",
      branch: "",
      account: "",
      accountCheckDigit: "",
      accountDocument: "",
      accountOwnerName: "",
    },
    validationSchema,
    onSubmit: async (values, ctx) => {
      console.log(values.bankId !== "001");
      console.log(
        values.accountCheckDigit === undefined ||
          values.accountCheckDigit === ""
      );
      if (
        values.bankId !== "001" &&
        (values.accountCheckDigit === undefined ||
          values.accountCheckDigit === "")
      ) {
        ctx.setFieldError(
          "accountCheckDigit",
          "É necessário inserir o número do dígito da conta."
        );
        return;
      }
      if (values.bankId === "001") {
        delete values.accountCheckDigit;
      }
      await saveBank(values);
    },
  });

  const getBanks = async (): Promise<void> => {
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    const params = {
      document,
      _expand: "installments",
      _offset: 0,
      _limit: 3,
    };

    const { status, data } = await api.get(`industrial/bill/v1/banks`, {
      params,
      headers,
    });

    if (status === 200) {
      const responseData = data || [];
      const banksResult = responseData?.reduce((prev, value) => {
        const prevNew = prev;
        const bancoFindCode = bancos?.find(
          (value2) => value2.code === value?.code
        );
        if (bancoFindCode) {
          prevNew.push({
            ...value,
            ...bancoFindCode,
          });
        }
        return prevNew;
      }, []);

      setListBanks(banksResult);
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getBanks();
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (selectedUnit?.bankId && listBanks?.length) {
      // Setar os campos no useFormik
      formik.setValues({
        bankId: selectedUnit?.bankId,
        branch: selectedUnit?.branch,
        account: selectedUnit?.account,
        accountCheckDigit: selectedUnit?.accountCheckDigit,
        accountDocument:
          selectedUnit?.accountDocument?.length > 0 &&
          selectedUnit?.accountDocument?.length < 12
            ? formatCpf(selectedUnit?.accountDocument)
            : formatCnpj(selectedUnit?.accountDocument),
        accountOwnerName: selectedUnit?.accountOwnerName,
      });
      setStatus("editDisabled");
    }
  }, [selectedUnit, listBanks]);

  const labelButton = useMemo(() => {
    switch (status) {
      case "editDisabled":
        return "Editar banco";
      default:
        return "Cadastrar banco";
    }
  }, [status]);

  const clickButton = (): void => {
    setStatus("edit");
  };

  const handleDocument = (event, handleChange): void => {
    const cursorStart = event.target.selectionStart;
    const cursorEnd = event.target.selectionEnd;
    const numbers = event.target.value.replace(/[^\d]/g, "");
    event.target.value = event.target.value.replace(/[^\d]/g, "");

    if (numbers.length > 0 && numbers.length < 12) {
      event.target.value = formatCpf(numbers);
    }
    if (numbers.length > 11) {
      event.target.value = formatCnpj(numbers);
    }

    cpfCnpjCursorAlign(event, cursorStart, cursorEnd);
    handleChange(event);
  };

  const saveBank = async (values: any): Promise<void> => {
    try {
      const headers = {
        access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
      };
      const body = {
        hasDirectDebit: true,
        bankId: values.bankId,
        branch: values.branch,
        account: values.account,
        accountCheckDigit: values.accountCheckDigit?.length
          ? values.accountCheckDigit
          : null,
        accountDocument: (values.accountDocument || "").replace(/[^\d]/g, ""),
        accountOwnerName: values.accountOwnerName,
      };

      await api.patch(
        `industrial/customer/v1/customers/me/units/${selectedUnit.unitId}`,
        body,
        {
          headers,
        }
      );

      let newUnitsList = [...unitsList];
      newUnitsList = newUnitsList.map((unitF) => {
        if (unitF.unitId === selectedUnit.unitId) {
          unitF = {
            ...unitF,
            bankId: values.bankId,
            branch: values.branch,
            account: values.account,
            accountCheckDigit: values.accountCheckDigit,
            hasDirectDebit: true,
            accountDocument: (values.accountDocument || "").replace(
              /[^\d]/g,
              ""
            ),
            accountOwnerName: values.accountOwnerName,
          };

          dispatch(toggleSelectedUnit(unitF));
        }

        return unitF;
      });

      dispatch(setUnitsList(newUnitsList));

      // Verifica se o banco selecionado tem código '104' ou '001'
      const selectedBank = listBanks.find(
        (bank: any) => bank.code === values.bankId
      );
      const shouldShowAlert =
        selectedBank?.code === "104" || selectedBank?.code === "001";

      setIsOpenModalAlert(shouldShowAlert);
    } catch (err) {
      if (err.response) {
        const erro = err.response.data;

        if (erro) {
          const { message } = err.response.data;
          const { code } = err.response.data;

          if (err.response.status !== 401 && code !== "422.033") {
            toast.error(message);
          } else {
            setIsOpenModalFail(true);
          }
        } else {
          toast.error("Ocorreu um erro interno na aplicação");
        }
      } else {
        setIsOpenModalFail(true);
      }
    }
  };

  const text = useMemo(() => {
    const textFilter = listBanks?.filter(
      (bank) => bank?.code === formik.values.bankId
    ) as any;

    if (textFilter) {
      return textFilter[0]?.mensagem;
    }

    return "";
  }, [formik.values, listBanks]);

  const deleteSuccessDA = (): void => {
    dispatch(
      toggleSelectedUnit({
        ...selectedUnit,
        hasDirectDebit: false,
        bankId: "",
        branch: "",
        account: "",
        accountCheckDigit: "",
        accountDocument: "",
        accountOwnerName: "",
      })
    );
  };

  const isCloseModalAlert = (): void => {
    setIsOpenModalAlert(false);
    setIsOpenModalSuccess(true);
  };

  useEffect(() => {
    formik.validateForm();
  }, [formik.values.bankId]);

  return (
    <Container>
      <Header>
        <Title>Vantagens do débito automático</Title>
        <Subtitle>
          Adicione mais praticidade ao seu dia-a-dia com o débito automático!
          Assim você não corre o risco de esquecer alguma fatura ou perder um
          vencimento.
        </Subtitle>
      </Header>
      <Body>
        <Form onSubmit={formik.handleSubmit}>
          <FormItem>
            <TextField
              label="Nome do titular"
              type="text"
              name="accountOwnerName"
              value={formik.values.accountOwnerName}
              onBlur={formik.handleBlur}
              errorCondition={
                formik.errors.accountOwnerName &&
                formik.touched.accountOwnerName
              }
              errorMessage={formik.errors.accountOwnerName}
              propsLeadingIcon={{
                size: 25,
              }}
              onChange={formik.handleChange}
              disabled={formik.isSubmitting || status === "editDisabled"}
            />
          </FormItem>
          <FormItem>
            <TextField
              label="CPF/CNPJ do titular"
              type="text"
              name="accountDocument"
              value={formik.values.accountDocument}
              onBlur={formik.handleBlur}
              errorCondition={
                formik.errors.accountDocument && formik.touched.accountDocument
              }
              errorMessage={formik.errors.accountDocument}
              propsLeadingIcon={{
                size: 25,
              }}
              onChange={(e) => handleDocument(e, formik.handleChange)}
              disabled={formik.isSubmitting || status === "editDisabled"}
            />
          </FormItem>
          <FormItem>
            <TextSelect
              label="Selecione o banco"
              name="bankId"
              type="text"
              value={formik.values.bankId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorCondition={formik.errors.bankId && formik.touched.bankId}
              errorMessage={formik.errors.bankId}
              disabled={formik.isSubmitting || status === "editDisabled"}
            >
              {listBanks.map((bank) => (
                <option key={bank?.code} value={bank?.code}>
                  {bank?.name}
                </option>
              ))}
            </TextSelect>
          </FormItem>
          <FormItem>
            <TextField
              label="Agência"
              type="text"
              name="branch"
              value={formik.values.branch}
              onBlur={formik.handleBlur}
              errorCondition={formik.errors.branch && formik.touched.branch}
              errorMessage={formik.errors.branch}
              maxLength={4}
              propsLeadingIcon={{
                size: 25,
              }}
              onChange={formik.handleChange}
              disabled={formik.isSubmitting || status === "editDisabled"}
            />
          </FormItem>
          <FormItem>
            <TextField
              label="Nº da conta"
              type="text"
              name="account"
              value={formik.values.account}
              onBlur={formik.handleBlur}
              errorCondition={formik.errors.account && formik.touched.account}
              errorMessage={formik.errors.account}
              propsLeadingIcon={{
                size: 25,
              }}
              onChange={formik.handleChange}
              disabled={formik.isSubmitting || status === "editDisabled"}
            />
          </FormItem>
          {formik.values.bankId !== "001" && (
            <FormItem>
              <TextField
                label="Nº do dígito"
                type="text"
                name="accountCheckDigit"
                value={formik.values.accountCheckDigit}
                onBlur={formik.handleBlur}
                errorCondition={
                  formik.errors.accountCheckDigit &&
                  formik.touched.accountCheckDigit
                }
                errorMessage={formik.errors.accountCheckDigit}
                propsLeadingIcon={{
                  size: 25,
                }}
                onChange={formik.handleChange}
                disabled={formik.isSubmitting || status === "editDisabled"}
              />
            </FormItem>
          )}
          {status === "editDisabled" ? (
            <>
              <ContainedButton
                TrailingIcon={AiOutlineArrowRight}
                propsTrailingIcon={{
                  color: "#fff",
                  size: 24,
                }}
                onClick={clickButton}
                disabled={isLoading || formik.isSubmitting}
              >
                {labelButton}
              </ContainedButton>
              <ContainedButtonWarn>
                <ContainedButton
                  LeadingIcon={BsTrash}
                  propsLeadingIcon={{
                    color: "#fff",
                    size: 24,
                  }}
                  onClick={() => setOpenModalDelete(true)}
                  disabled={isLoading || formik.isSubmitting}
                >
                  Excluir débito automático
                </ContainedButton>
              </ContainedButtonWarn>
            </>
          ) : (
            <ContainedButton
              TrailingIcon={AiOutlineArrowRight}
              propsTrailingIcon={{
                color: "#fff",
                size: 24,
              }}
              type="submit"
              disabled={
                isLoading ||
                formik.isSubmitting ||
                !formik.isValid ||
                !formik.dirty
              }
            >
              {labelButton}
            </ContainedButton>
          )}
        </Form>
      </Body>
      {isLoading && <Loading />}

      <DebitSuccessModal
        openModal={isOpenModalSuccess}
        modalFunction={(toggle) => {
          setIsOpenModalSuccess(toggle);
        }}
        text={text}
      />
      <DebitAlertModal
        openModal={isOpenModalAlert}
        modalFunction={isCloseModalAlert}
      />
      <DebitFailModal
        openModal={isOpenModalFail}
        modalFunction={setIsOpenModalFail}
      />
      <DebitDeleteModal
        openModal={openModalDelete}
        modalFunction={setOpenModalDelete}
        setOpenModalDebit={setOpenModalDebit}
        deleteSuccessDA={deleteSuccessDA}
      />
      <DebitFailModal
        openModal={openModalDebit}
        modalFunction={setOpenModalDebit}
      />
    </Container>
  );
};

export default DebitoAutomaticoForm;
